import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="font-serif text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're here to answer your questions and help you schedule an appointment. Reach out to us today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 text-center border border-blue-100 shadow-lg">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-6">
                <i className="ri-phone-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Call Us</h3>
              <p className="text-gray-600 mb-4">Monday - Friday: 9 AM - 5 PM<br/>Saturday: 10 AM - 2 PM</p>
              <a 
                href="tel:+14165551234"
                className="text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors duration-200"
              >
                (416) 555-1234
              </a>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 text-center border border-blue-100 shadow-lg">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-6">
                <i className="ri-mail-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Email Us</h3>
              <p className="text-gray-600 mb-4">We'll respond within 24 hours</p>
              <a 
                href="mailto:info@doctorclinic.ca"
                className="text-xl font-bold text-blue-600 hover:text-blue-700 transition-colors duration-200 break-all"
              >
                info@doctorclinic.ca
              </a>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 text-center border border-blue-100 shadow-lg">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-6">
                <i className="ri-map-pin-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Visit Us</h3>
              <p className="text-gray-600 mb-4">
                123 Medical Centre Drive<br/>
                Toronto, ON M5H 2N2<br/>
                Canada
              </p>
              <a 
                href="https://www.google.com/maps/dir//123+Medical+Centre+Drive+Toronto+ON+M5H+2N2"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-blue-600 hover:text-blue-700 font-semibold transition-colors duration-200"
              >
                Get Directions →
              </a>
            </div>
          </div>

          {/* Contact Form and Info */}
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Have a question or need assistance? Fill out the form below and our team will get back to you as soon as possible.
              </p>

              <form 
                data-readdy-form
                action="https://readdy.ai/api/form/d5fbh7k7bv8ec1oguhkg"
                method="POST"
                className="space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  
                  const name = formData.get('name') as string;
                  const email = formData.get('email') as string;
                  const phone = formData.get('phone') as string;
                  const message = formData.get('message') as string;
                  
                  if (!name || !email || !phone || !message) {
                    alert('Please fill in all required fields');
                    return;
                  }

                  fetch('https://readdy.ai/api/form/d5fbh7k7bv8ec1oguhkg', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString()
                  })
                  .then(response => {
                    if (response.ok) {
                      alert('Message sent successfully! We will get back to you soon.');
                      form.reset();
                    } else {
                      alert('There was an error sending your message. Please try again.');
                    }
                  })
                  .catch(() => {
                    alert('There was an error sending your message. Please try again.');
                  });
                }}
              >
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text"
                    name="name"
                    required
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="tel"
                      name="phone"
                      required
                      placeholder="(416) 555-1234"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Subject
                  </label>
                  <select 
                    name="subject"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Appointment Question">Appointment Question</option>
                    <option value="New Patient">New Patient Information</option>
                    <option value="Medical Records">Medical Records Request</option>
                    <option value="Billing Question">Billing Question</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea 
                    name="message"
                    rows={6}
                    required
                    maxLength={500}
                    placeholder="How can we help you?"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <button 
                  type="submit"
                  className="w-full bg-blue-900 hover:bg-blue-800 text-white py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Send Message
                </button>
              </form>
            </div>

            <div>
              <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">
                Office Hours
              </h2>
              
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 mb-8">
                <div className="space-y-4">
                  {[
                    { day: 'Monday', hours: '9:00 AM - 5:00 PM', isOpen: true },
                    { day: 'Tuesday', hours: '9:00 AM - 5:00 PM', isOpen: true },
                    { day: 'Wednesday', hours: '9:00 AM - 5:00 PM', isOpen: true },
                    { day: 'Thursday', hours: '9:00 AM - 5:00 PM', isOpen: true },
                    { day: 'Friday', hours: '9:00 AM - 5:00 PM', isOpen: true },
                    { day: 'Saturday', hours: '10:00 AM - 2:00 PM', isOpen: true },
                    { day: 'Sunday', hours: 'Closed', isOpen: false }
                  ].map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-gray-200 last:border-0">
                      <span className="font-semibold text-gray-900">{schedule.day}</span>
                      <span className={`font-medium ${schedule.isOpen ? 'text-gray-700' : 'text-gray-500'}`}>
                        {schedule.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-900 text-white rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold mb-4">Emergency?</h3>
                <p className="text-blue-100 mb-6 leading-relaxed">
                  For medical emergencies, please call 911 or visit your nearest emergency room immediately.
                </p>
                <div className="flex items-center gap-3 text-lg font-semibold">
                  <i className="ri-alarm-warning-fill text-2xl text-yellow-400"></i>
                  <span>Call 911 for Emergencies</span>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Parking & Accessibility</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-parking-box-line text-blue-600 text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Free parking available in building lot</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-wheelchair-line text-blue-600 text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Wheelchair accessible entrance and facilities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-bus-line text-blue-600 text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Public transit accessible (Bus routes 5, 12, 23)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-building-line text-blue-600 text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700">Located on ground floor for easy access</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="relative h-screen min-h-[600px]">
        <div className="absolute inset-0">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2886.267837056524!2d-79.38318708450221!3d43.65322097912108!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b34d68bf33a9b%3A0x15edd8c4de1c7581!2sCN%20Tower!5e0!3m2!1sen!2sca!4v1234567890123!5m2!1sen!2sca"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Clinic Location Map"
          ></iframe>
        </div>

        <div className="absolute bottom-8 left-8 bg-white rounded-2xl shadow-2xl p-8 max-w-md z-10">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Visit Our Clinic</h3>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-map-pin-fill text-blue-600 text-xl"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900">123 Medical Centre Drive</p>
                <p className="text-gray-600">Toronto, ON M5H 2N2, Canada</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-phone-fill text-blue-600 text-xl"></i>
              </div>
              <div>
                <a href="tel:+14165551234" className="text-lg font-semibold text-blue-600 hover:text-blue-700">
                  (416) 555-1234
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-mail-fill text-blue-600 text-xl"></i>
              </div>
              <div>
                <a href="mailto:info@doctorclinic.ca" className="text-blue-600 hover:text-blue-700">
                  info@doctorclinic.ca
                </a>
              </div>
            </div>
          </div>

          <a 
            href="https://www.google.com/maps/dir//123+Medical+Centre+Drive+Toronto+ON+M5H+2N2"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-blue-900 hover:bg-blue-800 text-white text-center py-3 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap"
          >
            Get Directions
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
